/**
 * Helpers for trimming quoted reply history from email bodies.
 *
 * When a customer replies to one of our emails, mail clients embed the full
 * original message at the bottom of the response (Outlook's "From: / Sent: /
 * To:" header block, Gmail's "On <date>, ... wrote:", etc.). Showing all of
 * that in the dashboard makes every reply look enormous and hides the
 * actually-new content. These helpers locate the start of the quoted block
 * and chop it off so we render only what the sender actually wrote.
 *
 * The proper fix lives in the backend (we now persist Microsoft Graph's
 * `uniqueBody`, which has the quoted thread already stripped server-side).
 * These helpers are kept as a defense-in-depth fallback for messages stored
 * before that change, or for HTML where uniqueBody happens to leave debris.
 */

const QUOTE_SELECTOR_IDS = ['#appendonsend', '#divRplyFwdMsg']
const QUOTE_SELECTOR_CLASSES = ['.gmail_quote']
const QUOTE_TAG_SELECTORS = ['blockquote']

/** Strip quoted thread history from an HTML email body. */
export function stripQuotedHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return html
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(html, 'text/html')
  } catch {
    return html
  }
  const body = doc.body
  const cutNode = findQuoteCutNode(body)
  if (!cutNode) return html

  // Walk from cutNode up to <body>, removing every later sibling at each level.
  // This drops the quoted block plus any wrapper noise that follows it (preview
  // banners, "external sender" warnings, the original quoted body, etc.).
  let node: Node | null = cutNode
  while (node && node !== body && node.parentNode) {
    const parent: Node = node.parentNode
    let sib = node.nextSibling
    while (sib) {
      const next = sib.nextSibling
      parent.removeChild(sib)
      sib = next
    }
    node = parent
  }
  cutNode.parentNode?.removeChild(cutNode)

  // Drop any trailing empty <p>/<div>/<br>/whitespace that the sender's mail
  // client added between their signature and the quote divider.
  trimTrailingEmpty(body)

  const result = body.innerHTML.trim()
  return result.length > 20 ? result : html
}

/** Strip quoted thread history from a plain-text email body. */
export function stripQuotedText(text: string): string {
  const patterns = [
    /\n\s*From:.*\n\s*Sent:/i,
    /\n\s*On .+ wrote:\s*\n/i,
    /\n-{3,}\s*Original Message/i,
  ]
  for (const pattern of patterns) {
    const match = text.search(pattern)
    if (match > 20) return text.slice(0, match).trim()
  }
  return text
}

/**
 * Locate the element that begins the quoted thread history, in document order.
 * Tries structural markers first (Outlook/Gmail/Apple Mail containers) and
 * falls back to detecting an Outlook desktop "border-top:solid" divider or
 * a generic "From: ... Sent: ... To: ..." header paragraph.
 */
function findQuoteCutNode(root: Element): Element | null {
  for (const selector of [...QUOTE_SELECTOR_IDS, ...QUOTE_SELECTOR_CLASSES, ...QUOTE_TAG_SELECTORS]) {
    const found = root.querySelector(selector)
    if (found) return found
  }

  // Outlook desktop: a <div> with inline style "border-top:solid ..." wraps
  // the From:/Sent:/To: header block.
  for (const div of Array.from(root.querySelectorAll<HTMLElement>('div[style*="border-top"]'))) {
    const style = div.getAttribute('style') || ''
    if (!/border-top\s*:\s*solid/i.test(style)) continue
    const text = (div.textContent || '').trim().slice(0, 200)
    if (/\bFrom\s*:/i.test(text)) return div
  }

  // Generic header: an element whose textContent begins with "From:" and
  // includes "Sent:" plus "To:" within the first ~500 chars. Catches Outlook
  // variants that don't use any of the markers above (e.g. <b><span>From:</span></b>
  // inside a plain MsoNormal paragraph with no surrounding divider).
  // Note: don't anchor "Sent"/"To" with \b — when labels are <br>-separated,
  // textContent runs adjacent values together (e.g. "...comSent:..."), and
  // <m> → <S> is not a word boundary.
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('p, div'))) {
    const text = (el.textContent || '').trim().slice(0, 500)
    if (/^From\s*:/i.test(text) && /Sent\s*:/i.test(text) && /To\s*:/i.test(text)) {
      return el
    }
  }

  return null
}

/** Remove trailing whitespace-only paragraphs/divs/breaks left behind after a cut. */
function trimTrailingEmpty(container: Element): void {
  while (container.lastChild) {
    const last = container.lastChild
    if (last.nodeType === 3 /* text node */) {
      if (!(last.textContent ?? '').replace(/\u00A0/g, '').trim()) {
        container.removeChild(last)
        continue
      }
      return
    }
    if (last.nodeType !== 1 /* element */) {
      container.removeChild(last)
      continue
    }
    const el = last as Element
    const tag = el.tagName.toLowerCase()
    if (tag === 'br') {
      container.removeChild(el)
      continue
    }
    if (tag === 'p' || tag === 'div') {
      const visible = (el.textContent ?? '').replace(/\u00A0/g, '').trim()
      const hasMedia = el.querySelector('img, video, table') !== null
      if (!visible && !hasMedia) {
        container.removeChild(el)
        continue
      }
      // Recurse into the last meaningful container so we trim trailing
      // whitespace inside it too (e.g. the empty <div> that wrapped the
      // border-top divider).
      trimTrailingEmpty(el)
      return
    }
    return
  }
}
